import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import teacherService from '../../services/api/teacher';
import { 
  Save, 
  Plus, 
  Trash2, 
  Clock,
  HelpCircle,
  ChevronDown,
  CheckCircle2,
  X,
  Zap,
  Target,
  FileText,
  Eye,
  Settings,
  ShieldCheck,
  ChevronLeft,
  Edit,
  MousePointerClick
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';

const CreateQuiz = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  // الحصول على lesson_id من الرابط (المنطق الأصلي)
  const preSelectedLessonId = searchParams.get('lesson');

  // حالة النموذج الأساسي
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    lessonId: preSelectedLessonId || '',
    duration: 30,
    passingScore: 60,
    maxAttempts: 3,
    showCorrectAnswers: true,
    isPublished: true,
    isActive: true,
    questions: []
  });

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    questionType: 'multiple_choice',
    explanation: '',
    isRequired: true,
    answers: [
      { text: '', isCorrect: true, order: 1 },
      { text: '', isCorrect: false, order: 2 }
    ]
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // جلب دروس المعلم (المنطق الأصلي كاملاً)
  const { data: lessonsData, isLoading: lessonsLoading } = useQuery(
    'teacher-lessons',
    () => teacherService.getTeacherLessons(),
    {
      retry: 1,
      select: (data) => {
        if (Array.isArray(data)) return data;
        if (data?.data && Array.isArray(data.data)) return data.data;
        if (data?.lessons && Array.isArray(data.lessons)) return data.lessons;
        return [];
      }
    }
  );

  const lessons = lessonsData || [];

  // إضافة سؤال جديد
  const handleAddQuestion = () => {
    setCurrentQuestionIndex(formData.questions.length);
    setQuestionForm({
      questionText: '',
      questionType: 'multiple_choice',
      explanation: '',
      isRequired: true,
      answers: [
        { text: '', isCorrect: true, order: 1 },
        { text: '', isCorrect: false, order: 2 }
      ]
    });
    setShowQuestionModal(true);
  };

  // تعديل سؤال
  const handleEditQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setQuestionForm(formData.questions[index]);
    setShowQuestionModal(true);
  };

  // حفظ السؤال في القائمة المؤقتة
  const handleSaveQuestion = () => {
    if (!questionForm.questionText.trim()) {
      toast.error('نص السؤال لا يمكن أن يكون فارغاً');
      return;
    }

    const validAnswers = questionForm.answers.filter(a => a.text.trim());
    if (validAnswers.length < 2) {
      toast.error('يجب توفير خيارين على الأقل لكل سؤال');
      return;
    }

    if (!validAnswers.some(a => a.isCorrect)) {
      toast.error('يرجى تحديد الخيار الصحيح لهذا السؤال');
      return;
    }

    const updatedQuestions = [...formData.questions];
    updatedQuestions[currentQuestionIndex] = {
      ...questionForm,
      answers: validAnswers.map((a, i) => ({ ...a, order: i + 1 })),
      order: currentQuestionIndex + 1
    };

    setFormData(prev => ({ ...prev, questions: updatedQuestions }));
    setShowQuestionModal(false);
    toast.success('تم حفظ السؤال في القائمة');
  };

  const handleDeleteQuestion = (index) => {
    if (window.confirm('هل تريد حذف هذا السؤال؟')) {
      setFormData(prev => ({
        ...prev,
        questions: prev.questions.filter((_, i) => i !== index)
      }));
      toast.success('تم الحذف من القائمة');
    }
  };

  const handleAddAnswer = () => {
    const newOrder = (questionForm.answers[questionForm.answers.length - 1]?.order || 0) + 1;
    setQuestionForm(prev => ({
      ...prev,
      answers: [...prev.answers, { text: '', isCorrect: false, order: newOrder }]
    }));
  };

  const handleUpdateAnswer = (index, field, value) => {
    const updatedAnswers = [...questionForm.answers];
    updatedAnswers[index] = { ...updatedAnswers[index], [field]: value };

    if (field === 'isCorrect' && value) {
      updatedAnswers.forEach((a, i) => { if (i !== index) a.isCorrect = false; });
    }

    setQuestionForm(prev => ({ ...prev, answers: updatedAnswers }));
  };

  const handleDeleteAnswer = (index) => {
    if (questionForm.answers.length > 2) {
      setQuestionForm(prev => ({
        ...prev,
        answers: prev.answers.filter((_, i) => i !== index)
      }));
    } else {
      toast.error('الحد الأدنى للخيارات هو خيارين');
    }
  };

  // تنفيذ عملية الإنشاء النهائية
  const createQuizMutation = useMutation(
    async (quizData) => teacherService.createQuiz(quizData),
    {
      onSuccess: () => {
        toast.success('تهانينا! تم إنشاء ونشر الاختبار بنجاح');
        queryClient.invalidateQueries('teacher-quizzes');
        navigate('/teacher/quizzes');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في إنشاء الاختبار');
        setIsSubmitting(false);
      }
    }
  );

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'عنوان الاختبار مطلوب';
    if (!formData.lessonId) newErrors.lessonId = 'يجب ربط الاختبار بدرس معين';
    if (formData.duration < 1) newErrors.duration = 'المدة يجب أن تكون دقيقة واحدة على الأقل';
    if (formData.questions.length === 0) newErrors.questions = 'لا يمكن إنشاء اختبار بدون أسئلة';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
        toast.error('يرجى مراجعة البيانات المدخلة');
        return;
    }
    setIsSubmitting(true);
    createQuizMutation.mutate(formData);
  };

  if (lessonsLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تجهيز بيانات الدروس...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-10 px-4 sm:px-6 py-8 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
              <div className="bg-primary-600 text-white p-2 rounded-xl shadow-lg shadow-primary-200">
                <Plus size={28} />
              </div>
              إنشاء اختبار ذكي
            </h1>
            <p className="text-gray-500 font-medium flex items-center gap-2">
                <ShieldCheck size={18} className="text-emerald-500" />
                قم ببناء اختبارات تفاعلية لتقييم مستوى استيعاب الطلاب
            </p>
          </div>
          <button 
            onClick={() => navigate('/teacher/quizzes')}
            className="group flex items-center text-gray-400 hover:text-primary-600 font-bold transition-all text-sm"
          >
            إلغاء والعودة <ChevronLeft className="mr-1 group-hover:translate-x-[-4px] transition-transform" />
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Main Content: Info & Questions (8 Columns) */}
        <div className="lg:col-span-8 space-y-8">
            
            {/* 1. Information Card */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 space-y-8">
                <h2 className="text-xl font-black text-gray-900 flex items-center gap-3 uppercase tracking-tighter">
                    <FileText size={22} className="text-primary-600" /> البيانات التعريفية
                </h2>

                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">عنوان الاختبار الرئيسي *</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            className={`w-full px-6 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 ${errors.title ? 'border-red-300' : 'border-gray-100'}`}
                            placeholder="مثال: اختبار شامل على الوحدة الأولى"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">وصف أو تعليمات الاختبار</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows="3"
                            className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-medium text-gray-700 resize-none"
                            placeholder="أدخل أي ملاحظات تود أن يراها الطالب قبل البدء..."
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">الدرس المرتبط بالاختبار *</label>
                        <div className="relative">
                            <select
                                value={formData.lessonId}
                                onChange={(e) => setFormData(prev => ({ ...prev, lessonId: e.target.value }))}
                                className={`w-full px-6 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer ${errors.lessonId ? 'border-red-300' : 'border-gray-100'}`}
                            >
                                <option value="">اختر الدرس من القائمة...</option>
                                {lessons.map(lesson => (
                                    <option key={lesson.id} value={lesson.id}>{lesson.title}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. Questions Builder Section */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 space-y-8">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-black text-gray-900 flex items-center gap-3 uppercase tracking-tighter">
                        <Zap size={22} className="text-primary-600" /> بنك أسئلة الاختبار
                    </h2>
                    <button
                        type="button"
                        onClick={handleAddQuestion}
                        className="bg-primary-50 text-primary-600 px-6 py-2.5 rounded-xl font-black text-xs hover:bg-primary-600 hover:text-white transition-all flex items-center gap-2 border border-primary-100 shadow-sm"
                    >
                        <Plus size={16} /> إضافة سؤال
                    </button>
                </div>

                {formData.questions.length === 0 ? (
                    <div className="py-20 text-center bg-gray-50/50 rounded-[2.5rem] border border-dashed border-gray-200">
                        <HelpCircle size={48} className="text-gray-200 mx-auto mb-4" />
                        <p className="text-gray-500 font-bold">لم تقم بإضافة أي أسئلة لهذا الاختبار بعد.</p>
                        <button type="button" onClick={handleAddQuestion} className="mt-4 text-primary-600 font-black text-sm uppercase tracking-widest hover:underline">انقر للبدء الآن</button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {formData.questions.map((question, index) => (
                            <div key={index} className="group bg-white border border-gray-100 rounded-[2rem] p-6 hover:shadow-xl hover:border-primary-100 transition-all duration-300 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-1.5 h-full bg-primary-600 opacity-10 group-hover:opacity-100 transition-opacity"></div>
                                <div className="flex items-start justify-between gap-6">
                                    <div className="space-y-3 flex-1">
                                        <div className="flex items-center gap-3">
                                            <span className="bg-gray-900 text-white w-8 h-8 flex items-center justify-center rounded-xl font-black text-xs">
                                                {index + 1}
                                            </span>
                                            {question.isRequired && (
                                                <span className="bg-rose-50 text-rose-600 px-3 py-1 rounded-lg text-[9px] font-black uppercase border border-rose-100">سؤال إلزامي</span>
                                            )}
                                        </div>
                                        <p className="text-lg font-bold text-gray-800 leading-relaxed">{question.questionText}</p>
                                        
                                        <div className="flex flex-wrap gap-2 pt-2">
                                            {question.answers?.map((ans, idx) => (
                                                <div key={idx} className={`px-3 py-1.5 rounded-xl text-[10px] font-bold border transition-all ${ans.isCorrect ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-400 border-gray-100'}`}>
                                                    {ans.text}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="flex flex-col gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleEditQuestion(index)}
                                            className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                                        >
                                            <Edit size={18} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteQuestion(index)}
                                            className="w-10 h-10 bg-red-50 text-red-600 rounded-xl flex items-center justify-center hover:bg-red-600 hover:text-white transition-all shadow-sm"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {errors.questions && <p className="text-rose-600 text-xs font-black uppercase text-center mt-4">⚠️ {errors.questions}</p>}
            </div>
        </div>

        {/* Sidebar: Settings & Actions (4 Columns) */}
        <div className="lg:col-span-4 space-y-6">
            
            {/* 3. Settings Card */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
                <h3 className="text-lg font-black text-gray-900 mb-8 flex items-center gap-3 uppercase tracking-tighter">
                    <Settings size={20} className="text-primary-600" /> إعدادات الاختبار
                </h3>

                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">مدة الاختبار (بالدقائق)</label>
                        <div className="relative">
                            <input
                                type="number"
                                value={formData.duration}
                                onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-black text-gray-900"
                            />
                            <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">نسبة النجاح المطلوبة (%)</label>
                        <div className="relative">
                            <input
                                type="number"
                                value={formData.passingScore}
                                onChange={(e) => setFormData(prev => ({ ...prev, passingScore: parseInt(e.target.value) }))}
                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-black text-gray-900"
                            />
                            <Target className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">المحاولات المسموحة</label>
                        <div className="relative">
                            <input
                                type="number"
                                value={formData.maxAttempts}
                                onChange={(e) => setFormData(prev => ({ ...prev, maxAttempts: parseInt(e.target.value) }))}
                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-black text-gray-900"
                            />
                            <RefreshCwIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                        </div>
                    </div>

                    <div className="pt-4 space-y-4 border-t border-gray-50">
                        {[
                            { id: 'showCorrectAnswers', label: 'كشف الإجابات بعد الحل', icon: Eye },
                            { id: 'isActive', label: 'الاختبار نشط حالياً', icon: Zap },
                            { id: 'isPublished', label: 'نشر الاختبار للطلاب', icon: CheckCircle2 }
                        ].map((toggle) => (
                            <div key={toggle.id} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-gray-100 group transition-all hover:bg-white">
                                <div className="flex items-center gap-3">
                                    <toggle.icon size={16} className="text-primary-500" />
                                    <span className="text-xs font-bold text-gray-700">{toggle.label}</span>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData[toggle.id]}
                                        onChange={(e) => setFormData(prev => ({ ...prev, [toggle.id]: e.target.checked }))}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 4. Final Action Hub */}
            <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
                <div className="relative z-10 space-y-8 text-center">
                    <div className="bg-primary-600 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto shadow-xl shadow-primary-900/50 group-hover:rotate-12 transition-transform">
                        <Save size={28} />
                    </div>
                    <div className="space-y-2">
                        <h4 className="text-2xl font-black tracking-tight leading-tight">تأكيد الحفظ</h4>
                        <p className="text-gray-400 text-sm font-medium leading-relaxed">
                            تأكد من مراجعة كافة الأسئلة والخيارات قبل الحفظ النهائي لضمان جودة الاختبار.
                        </p>
                    </div>
                    <button
                        type="submit"
                        disabled={isSubmitting || createQuizMutation.isLoading}
                        className="w-full bg-white text-gray-900 py-5 rounded-[1.8rem] font-black text-lg hover:bg-primary-50 transition-all transform active:scale-95 shadow-xl flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                        {isSubmitting ? <RefreshCwIcon className="animate-spin" size={24} /> : <Zap size={24} fill="currentColor" />}
                        نشر الاختبار الآن
                    </button>
                    <button 
                      type="button" 
                      onClick={() => navigate('/teacher/quizzes')}
                      className="text-xs font-bold text-gray-500 uppercase tracking-[0.2em] hover:text-white transition-colors"
                    >
                      حفظ كمسودة والعودة
                    </button>
                </div>
            </div>
        </div>
      </form>

      {/* Modern Question Modal */}
      {showQuestionModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[300] p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full relative overflow-hidden transition-all transform scale-100">
            
            {/* Modal Header */}
            <div className="p-8 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-primary-100">
                    <HelpCircle size={24} />
                </div>
                <div>
                    <h3 className="text-xl font-black text-gray-900 leading-none">
                        {currentQuestionIndex < formData.questions.length ? 'تعديل السؤال' : 'إضافة سؤال جديد'}
                    </h3>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1.5">بناء بنية السؤال التعليمي</p>
                </div>
              </div>
              <button onClick={() => setShowQuestionModal(false)} className="w-10 h-10 flex items-center justify-center bg-white text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all border border-gray-100">
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-8 lg:p-10 space-y-8 max-h-[70vh] overflow-y-auto scrollbar-thin">
              
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">نص السؤال الرئيسي *</label>
                <textarea
                  value={questionForm.questionText}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, questionText: e.target.value }))}
                  rows="3"
                  className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 resize-none leading-relaxed"
                  placeholder="اكتب السؤال بوضوح هنا..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">نوع السؤال</label>
                  <select
                    value={questionForm.questionType}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, questionType: e.target.value }))}
                    className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-bold text-gray-700"
                  >
                    <option value="multiple_choice">اختيار من متعدد</option>
                    <option value="true_false">صح أو خطأ</option>
                    <option value="short_answer">إجابة قصيرة</option>
                  </select>
                </div>

                <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 flex items-center justify-between mt-6">
                    <span className="text-xs font-black text-gray-700 uppercase tracking-tighter">سؤال إلزامي للحل</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={questionForm.isRequired}
                            onChange={(e) => setQuestionForm(prev => ({ ...prev, isRequired: e.target.checked }))}
                            className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-600"></div>
                    </label>
                </div>
              </div>

              {/* Answers Builder Section */}
              <div className="space-y-6 pt-6 border-t border-gray-50">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-black text-gray-900 uppercase tracking-tighter">خيارات الإجابة</h4>
                  <button
                    type="button"
                    onClick={handleAddAnswer}
                    className="text-primary-600 font-black text-[10px] uppercase tracking-widest bg-primary-50 px-4 py-2 rounded-xl hover:bg-primary-600 hover:text-white transition-all"
                  >
                    + إضافة خيار
                  </button>
                </div>

                <div className="space-y-3">
                    {questionForm.answers.map((answer, index) => (
                    <div key={index} className={`flex items-center gap-4 p-4 rounded-3xl border-2 transition-all group ${answer.isCorrect ? 'border-emerald-500 bg-emerald-50/30' : 'border-gray-50 bg-gray-50/30 hover:border-gray-200'}`}>
                        <button
                            type="button"
                            onClick={() => handleUpdateAnswer(index, 'isCorrect', true)}
                            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all shadow-sm ${answer.isCorrect ? 'bg-emerald-600 text-white' : 'bg-white text-gray-300 hover:text-emerald-500'}`}
                        >
                            {answer.isCorrect ? <CheckCircle2 size={24} /> : <MousePointerClick size={20} />}
                        </button>
                        
                        <input
                            type="text"
                            value={answer.text}
                            onChange={(e) => handleUpdateAnswer(index, 'text', e.target.value)}
                            className={`flex-1 bg-transparent border-none font-bold text-gray-800 focus:ring-0 placeholder:text-gray-300 placeholder:font-medium`}
                            placeholder={`اكتب الخيار ${String.fromCharCode(65 + index)} هنا...`}
                        />

                        {questionForm.answers.length > 2 && (
                        <button
                            type="button"
                            onClick={() => handleDeleteAnswer(index)}
                            className="p-2 text-gray-300 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                        >
                            <Trash2 size={18} />
                        </button>
                        )}
                    </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-8 border-t border-gray-100 bg-gray-50/50 grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setShowQuestionModal(false)}
                className="px-6 py-4 border border-gray-200 rounded-2xl text-gray-400 font-black text-sm hover:bg-gray-100 transition-all uppercase tracking-widest"
              >
                تجاهل
              </button>
              <button
                type="button"
                onClick={handleSaveQuestion}
                className="px-6 py-4 bg-primary-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-2 transform active:scale-95"
              >
                <Save size={18} /> تأكيد الحفظ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// أيقونات إضافية
const RefreshCwIcon = ({ size, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
    </svg>
);

export default CreateQuiz;